import React, { useState, useEffect } from "react";
import { useData } from '../../hooks/useData';
import { Upload, AlertTriangle } from 'lucide-react';
import styled from "styled-components";
import { isValidFile, isValidFileType } from '../../utils/fileTools';

const KILO_BYTES_PER_BYTE = 1000;
const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 5000000;
const ariaLabel = { 'aria-label': 'description' };

/**
 * FileUploadFullScreen - A fullscreen drag-and-drop overlay component
 * 
 * This component creates a transparent overlay that covers the entire application
 * and becomes visible when files are dragged over it. It's designed to be placed
 * at the highest level of the app hierarchy (typically in App.js).
 * 
 * Features:
 * - Transparent by default, only visible during drag operations
 * - Smooth animations and visual feedback
 * - File size validation
 * - File type validation
 * - Callback function for handling dropped files
 * 
 * Usage:
 * <FileUploadFullScreen 
 *   maxFileSizeInBytes={5000000} // 5MB default
 * />
 * 
 * Props:
 * - onFilesDrop: Function called with array of valid files when files are dropped
 * - maxFileSizeInBytes: Maximum file size in bytes (default: 5MB)
 */

const FullScreenOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: transparent;
	z-index: 999999;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	transition: all 0.3s ease;
    

	${props => props.$isDragging && `
		background: rgba(135, 206, 250, 0.2);
		pointer-events: all;
		
		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background: rgba(56, 88, 108, 0.77);
			backdrop-filter: blur(4px);
		}
	`}
`;

const DropZone = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem;
	border: 3px dashed rgba(135, 206, 250, 0.0);
	border-radius: 20px;
	background: rgba(40, 40, 40, 0.0);
	backdrop-filter: blur(0px);
	transition: all 0.3s ease;
	opacity: 0;
	transform: scale(0.9);

	${props => props.$isDragging && !props.$hasInvalidFiles && `
		opacity: 1;
		transform: scale(1);
		border-color: rgba(135, 206, 250, 0.0);
		background: rgba(255, 255, 255, 0.0);
		box-shadow: 0 10px 20px rgba(135, 206, 250, 0);
	`}

	${props => props.$isDragging && props.$hasInvalidFiles && `
		opacity: 1;
		transform: scale(1);
		border-color: rgba(255, 193, 7, 0.0);
		background: rgba(255, 193, 7, 0.0);
		box-shadow: 0 10px 20px rgba(255, 193, 7, 0);
	`}
`;

const UploadIcon = styled(Upload)`
	color: rgba(135, 206, 250, 0.8);
	margin-bottom: 1rem;
	transition: all 0.3s ease;

	${props => props.$isDragging && !props.$hasInvalidFiles && `
		color: rgba(135, 206, 250, 1);
		transform: scale(1.2);
	`}
`;

const CautionIcon = styled(AlertTriangle)`
	color: rgba(255, 193, 7, 0.8);
	margin-bottom: 1rem;
	transition: all 0.3s ease;

	${props => props.$isDragging && props.$hasInvalidFiles && `
		color: rgba(255, 193, 7, 1);
		transform: scale(1.2);
	`}
`;

const DropText = styled.div`
	color: rgba(135, 206, 250, 0.8);
	font-size: 1.5rem;
	font-weight: 600;
	text-align: center;
	transition: all 0.3s ease;

	${props => props.$isDragging && !props.$hasInvalidFiles && `
		color: rgba(135, 206, 250, 1);
	`}

	${props => props.$isDragging && props.$hasInvalidFiles && `
		color: rgba(255, 193, 7, 1);
	`}
`;

const FileUploadFullScreen = ({
	onFilesDrop,
	maxFileSizeInBytes = 5000000,
	...otherProps
}) => {
	const [isDragging, setIsDragging] = useState(false);
	const [hasInvalidFiles, setHasInvalidFiles] = useState(false);
    const {handleFilesUpload, files} = useData();

	const addNewFiles = (newFiles) => {
		const validFiles = Array.from(newFiles).filter(file => isValidFile(file, maxFileSizeInBytes));
		return validFiles;
	};

	
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set dragging to false if we're leaving the window entirely
        if (e.clientX <= 0 || e.clientY <= 0 || 
            e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
            setIsDragging(false);
            setTimeout(() => {
                setHasInvalidFiles(false);
            },500);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // This is crucial - prevent the browser from trying to navigate to the file
        e.dataTransfer.dropEffect = 'copy';
        
        if (!isDragging) {
            setIsDragging(true);
        }

        // Check if any files are invalid
        if (e.dataTransfer.items) {
                        const hasInvalid = Array.from(e.dataTransfer.items).some(item => {
                                if (item.kind === 'file') {
                    // Try to get the file, but also check the type directly
                    const file = item.getAsFile();
                    const type = item.type;
                                        
                    if (file) {
                                                const isValid = isValidFile(file, maxFileSizeInBytes);
                                                return !isValid;
                    } else {
                        // If we can't get the file, check the type from the item
                        const validTypes = ['application/EDI-X12', 'text/plain', 'image/jpeg', 'image/jpg'];
                        const isValidType = validTypes.includes(type);
                        
                        // For .x12 files, they often have no MIME type or are text/plain
                        // So we'll be more permissive during drag operations
                        const isValid = isValidType || type === '' || type === 'text/plain';
                                                return !isValid;
                    }
                }
                return false;
            });
                        setHasInvalidFiles(hasInvalid);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setHasInvalidFiles(false);

        const { files: droppedFiles } = e.dataTransfer;
                if (droppedFiles.length) {
            const validFiles = addNewFiles(droppedFiles);
                        if (validFiles.length > 0 && onFilesDrop) {
                onFilesDrop(validFiles);
            }
        }
    };

	// Add global event listeners to the document
	useEffect(() => {
		

		// Add event listeners to document with passive: false to ensure preventDefault works
		document.addEventListener('dragenter', handleDragEnter, { passive: false });
		document.addEventListener('dragleave', handleDragLeave, { passive: false });
		document.addEventListener('dragover', handleDragOver, { passive: false });
		document.addEventListener('drop', handleDrop, { passive: false });

		// Cleanup
		return () => {
			document.removeEventListener('dragenter', handleDragEnter);
			document.removeEventListener('dragleave', handleDragLeave);
			document.removeEventListener('dragover', handleDragOver);
			document.removeEventListener('drop', handleDrop);
		};


	}, [isDragging, onFilesDrop]);

	return (
		<FullScreenOverlay $isDragging={isDragging}>
			<DropZone $isDragging={isDragging} $hasInvalidFiles={hasInvalidFiles}>
				{hasInvalidFiles ? (
					<>
						<CautionIcon size={44} $isDragging={isDragging} $hasInvalidFiles={hasInvalidFiles} />
						<DropText $isDragging={isDragging} $hasInvalidFiles={hasInvalidFiles}>
							Only .x12 files are allowed
						</DropText>
					</>
				) : (
					<>
						<UploadIcon size={44} $isDragging={isDragging} $hasInvalidFiles={hasInvalidFiles} />
						<DropText $isDragging={isDragging} $hasInvalidFiles={hasInvalidFiles}>
							Drop files to upload
						</DropText>
					</>
				)}
			</DropZone>
		</FullScreenOverlay>
	);
};

export default FileUploadFullScreen;